/**
 * **Ketersediaan menu per OUTLET** — menyalakan/mematikan departemen, kategori, atau produk.
 *
 * ### ⭐ Kasus yang melahirkannya (Imron)
 * Departemen **"Alcoholic"** menyala di PB-KBS, **mati** di PB-PKB karena izinnya berbeda. Begitu
 * juga di tingkat kategori — dan bila sebuah kategori dimatikan di satu outlet, **seluruh
 * produknya** ikut tak ada di sana.
 *
 * ### ⛔ Yang ditampilkan adalah PENGECUALIAN, bukan matriks
 * Bawaannya seluruh menu brand tersedia di seluruh outletnya. Sakelar di sini menambah/menghapus
 * satu baris pengecualian. Matriks penuh (nyalakan satu per satu) berakhir sebagai menu baru yang
 * tak pernah muncul di mana pun karena tak ada yang ingat menyalakannya di 40 outlet.
 *
 * ⚠️ Cascade dihitung SERVER saat membaca katalog. Layar ini menandainya supaya orang tahu kenapa
 * sebuah produk mati padahal sakelarnya sendiri menyala.
 */
import { Alert, Card, Empty, Select, Space, Table, Tag, Typography, message } from 'antd';

import { OnOff } from '../components/OnOff';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import type { Category, Department, MenuBlock, Outlet, Product } from '../api/client';
import { canWrite } from '../api/session';

type Baris =
  | { readonly kind: 'department'; readonly id: string; readonly code: string; readonly name: string }
  | { readonly kind: 'category'; readonly id: string; readonly code: string; readonly name: string;
      readonly parentBlocked: boolean }
  | { readonly kind: 'product'; readonly id: string; readonly code: string; readonly name: string;
      readonly parentBlocked: boolean };

export function Availability() {
  const { t } = useTranslation();
  const boleh = canWrite();

  const [outlet, setOutlet] = useState<readonly Outlet[]>([]);
  const [outletId, setOutletId] = useState<string | null>(null);
  const [block, setBlock] = useState<readonly MenuBlock[]>([]);
  const [department, setDepartment] = useState<readonly Department[]>([]);
  const [depId, setDepId] = useState<string | null>(null);
  const [category, setCategory] = useState<readonly Category[]>([]);
  const [katId, setKatId] = useState<string | null>(null);
  const [product, setProduct] = useState<readonly Product[]>([]);

  useEffect(() => { void api.outlets().then(setOutlet).catch(() => undefined); }, []);

  /* ⚠️ Departemen disaring per BRAND outlet terpilih — outlet tak pernah menjual menu brand lain. */
  useEffect(() => {
    const o = outlet.find((x) => x.id === outletId);
    if (o === undefined) { setDepartment([]); return; }
    void api.departments((o as unknown as { brand_id?: string }).brand_id)
      .then(setDepartment)
      .catch(() => undefined);
  }, [outlet, outletId]);

  useEffect(() => {
    if (depId === null) { setCategory([]); return; }
    void api.categories(depId).then(setCategory).catch(() => undefined);
  }, [depId]);
  useEffect(() => {
    if (katId === null) { setProduct([]); return; }
    void api.products(katId).then(setProduct).catch(() => undefined);
  }, [katId]);

  const muatBlock = useCallback(() => {
    if (outletId === null) { setBlock([]); return; }
    void api.blocks(outletId).then(setBlock).catch(() => undefined);
  }, [outletId]);
  useEffect(() => { muatBlock(); }, [muatBlock]);

  const cari = (kind: Baris['kind'], id: string): MenuBlock | undefined =>
    block.find((b) =>
      (kind === 'department' && b.department_id === id)
      || (kind === 'category' && b.category_id === id)
      || (kind === 'product' && b.product_id === id));

  const ubah = async (kind: Baris['kind'], id: string, tersedia: boolean) => {
    if (outletId === null) return;
    try {
      const ada = cari(kind, id);
      if (tersedia) {
        if (ada !== undefined) await api.unblock(ada.id);
      } else if (ada === undefined) {
        await api.block({
          outletId,
          ...(kind === 'department' ? { departmentId: id } : {}),
          ...(kind === 'category' ? { categoryId: id } : {}),
          ...(kind === 'product' ? { productId: id } : {}),
        });
      }
      muatBlock();
    } catch (e) {
      void message.error(e instanceof Error ? e.message : String(e));
    }
  };

  const depBlocked = depId !== null && cari('department', depId) !== undefined;
  const katBlocked = katId !== null && cari('category', katId) !== undefined;

  const kolom = (judul: string, testid: string, rows: readonly Baris[]) => (
    <Card size="small" title={judul} data-testid={`avail-${testid}`}>
      <Table<Baris>
        size="small"
        rowKey="id"
        pagination={false}
        dataSource={[...rows]}
        columns={[
          {
            title: judul,
            key: 'nama',
            render: (_: unknown, r: Baris) => (
              <Space>
                <Typography.Text type="secondary">{r.code}</Typography.Text>
                {r.name}
                {r.kind !== 'department' && r.parentBlocked && (
                  /* ⭐ Menjelaskan KENAPA mati padahal sakelarnya sendiri menyala. */
                  <Tag color="orange" data-testid={`warisan-${r.code}`}>{t('availability.inherited')}</Tag>
                )}
              </Space>
            ),
          },
          {
            title: t('availability.available'),
            key: 'sakelar',
            width: 120,
            render: (_: unknown, r: Baris) => {
              const diblokir = cari(r.kind, r.id) !== undefined;
              const warisan = r.kind !== 'department' && r.parentBlocked;
              return (
                <OnOff
                  testId={`sakelar-${r.code}`}
                  checked={!diblokir && !warisan}
                  disabled={!boleh || warisan}
                  onChange={(v) => void ubah(r.kind, r.id, v)}
                />
              );
            },
          },
        ]}
      />
    </Card>
  );

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }} data-testid="availability">
      <Space>
        <Typography.Text strong>{t('availability.outlet')}</Typography.Text>
        <span data-testid="pilih-outlet">
          <Select
            style={{ width: 260 }}
            placeholder={t('availability.outlet')}
            value={outletId}
            onChange={(v: string) => { setOutletId(v); setDepId(null); setKatId(null); }}
            options={outlet.map((o) => ({ value: o.id, label: `${o.code} · ${o.name}` }))}
          />
        </span>
      </Space>
      <Alert type="info" showIcon title={t('availability.note')} data-testid="availability-catatan" />

      {outletId === null ? (
        <Empty description={t('routing.pickOutlet')} data-testid="avail-pilih-outlet" />
      ) : (
        <>
          {kolom(t('master.departments'), 'dep', department.map((d) => ({
            kind: 'department' as const, id: d.id, code: d.code, name: d.name,
          })))}

          <Space>
            <Typography.Text>{t('master.departments')}</Typography.Text>
            <span data-testid="pilih-dep">
              <Select
                style={{ width: 220 }}
                value={depId}
                onChange={(v: string) => { setDepId(v); setKatId(null); }}
                options={department.map((d) => ({ value: d.id, label: `${d.code} · ${d.name}` }))}
              />
            </span>
            <span data-testid="pilih-kat">
              <Select
                style={{ width: 220 }}
                value={katId}
                disabled={depId === null}
                onChange={setKatId}
                options={category.map((c) => ({ value: c.id, label: `${c.code} · ${c.name}` }))}
              />
            </span>
          </Space>

          {depId !== null && kolom(t('master.categories'), 'kat', category.map((c) => ({
            kind: 'category' as const, id: c.id, code: c.code, name: c.name, parentBlocked: depBlocked,
          })))}

          {katId !== null && kolom(t('master.products'), 'prod', product.map((p) => ({
            kind: 'product' as const, id: p.id, code: p.code, name: p.name,
            parentBlocked: depBlocked || katBlocked,
          })))}
        </>
      )}
    </Space>
  );
}
